/**
 * Session Refresh Utility
 *
 * Implements periodic session refresh to keep user roles and permissions up-to-date
 * in localStorage. This ensures that role changes made by admins are reflected in
 * the user's session without requiring logout/login.
 *
 * Issue #860: Part of RBAC migration to handle stale role data
 *
 * @see docs/RBAC_MIGRATION.md
 * @see src/utils/rbac.ts
 */

import { authApi } from '../services/api';

/**
 * Configuration for session refresh
 */
export const SESSION_REFRESH_CONFIG = {
  // Refresh interval in milliseconds (5 minutes by default)
  REFRESH_INTERVAL_MS: 5 * 60 * 1000,

  // Key for storing last refresh timestamp in localStorage
  LAST_REFRESH_KEY: 'session_last_refresh',

  // Minimum time between refreshes (prevents too-frequent updates)
  MIN_REFRESH_INTERVAL_MS: 60 * 1000, // 1 minute
};

/**
 * Refresh the current user's session data
 * Fetches updated user profile from backend and updates localStorage
 *
 * @returns Promise<boolean> - true if refresh succeeded, false otherwise
 */
export async function refreshUserSession(): Promise<boolean> {
  try {
    // Check if user is logged in
    if (!authApi.isLoggedIn()) {
      return false;
    }

    // Check minimum refresh interval
    const lastRefresh = localStorage.getItem(SESSION_REFRESH_CONFIG.LAST_REFRESH_KEY);
    if (lastRefresh) {
      const timeSinceLastRefresh = Date.now() - parseInt(lastRefresh, 10);
      if (timeSinceLastRefresh < SESSION_REFRESH_CONFIG.MIN_REFRESH_INTERVAL_MS) {
        console.debug('[SessionRefresh] Skipping refresh - too soon since last refresh');
        return false;
      }
    }

    // Fetch updated user profile
    const userProfile = await authApi.getCurrentUser();

    // Update user data in localStorage
    localStorage.setItem('user', JSON.stringify(userProfile));

    // Legacy flag storage removed in Phase 4 (Issue #865)
    // All role checking now uses RBAC utilities in src/utils/rbac.ts

    // Update last refresh timestamp
    localStorage.setItem(SESSION_REFRESH_CONFIG.LAST_REFRESH_KEY, String(Date.now()));

    console.debug('[SessionRefresh] Session refreshed successfully', {
      roles: userProfile.roles || [],
      email: userProfile.email
    });

    return true;
  } catch (error) {
    console.error('[SessionRefresh] Failed to refresh session:', error);

    // If we get a 401, the token is invalid - logout
    if (error && typeof error === 'object' && 'response' in error) {
      const httpError = error as { response?: { status?: number } };
      if (httpError.response?.status === 401) {
        console.warn('[SessionRefresh] Token invalid - logging out');
        authApi.logout();
      }
    }

    return false;
  }
}

/**
 * Start periodic session refresh
 * Sets up an interval to refresh user session data periodically
 *
 * @returns Function to stop the refresh interval
 */
export function startSessionRefresh(): () => void {
  console.debug('[SessionRefresh] Starting periodic session refresh', {
    intervalMs: SESSION_REFRESH_CONFIG.REFRESH_INTERVAL_MS
  });

  // Perform initial refresh after a short delay (10 seconds)
  const initialTimeout = setTimeout(() => {
    refreshUserSession();
  }, 10000);

  // Set up periodic refresh
  const intervalId = setInterval(() => {
    refreshUserSession();
  }, SESSION_REFRESH_CONFIG.REFRESH_INTERVAL_MS);

  // Return cleanup function
  return () => {
    clearTimeout(initialTimeout);
    clearInterval(intervalId);
    console.debug('[SessionRefresh] Stopped periodic session refresh');
  };
}

/**
 * Force an immediate session refresh
 * Useful for manually triggering a refresh after specific actions
 *
 * @returns Promise<boolean> - true if refresh succeeded, false otherwise
 */
export async function forceSessionRefresh(): Promise<boolean> {
  console.debug('[SessionRefresh] Forcing immediate session refresh');

  // Bypass minimum refresh interval check by removing timestamp
  localStorage.removeItem(SESSION_REFRESH_CONFIG.LAST_REFRESH_KEY);

  return await refreshUserSession();
}

/**
 * Get time until next scheduled refresh
 *
 * @returns number - milliseconds until next refresh, or 0 if refresh should happen now
 */
export function getTimeUntilNextRefresh(): number {
  const lastRefresh = localStorage.getItem(SESSION_REFRESH_CONFIG.LAST_REFRESH_KEY);

  if (!lastRefresh) {
    return 0; // No previous refresh, should refresh now
  }

  const lastRefreshTime = parseInt(lastRefresh, 10);
  const nextRefreshTime = lastRefreshTime + SESSION_REFRESH_CONFIG.REFRESH_INTERVAL_MS;
  const timeUntilRefresh = nextRefreshTime - Date.now();

  return Math.max(0, timeUntilRefresh);
}
